import { useState } from "react";

import { ApiError } from "@/api/client";
import { useSetTenantUserPasswordMutation } from "@/api/tenants";
import { Button, ModalShell, Ribbon } from "@/components/ui";
import { t } from "@/i18n";

interface PasswordResetModalProps {
  userId: number;
  email: string;
  onClose: () => void;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.message) return error.message;
  return fallback;
}

/**
 * Mevcut üye için şifre sıfırlama modalı — eski `legacy/admin/users.html:
 * 108-152` + `legacy/js/admin-users.js:167-265` 🔑 modalinin karşılığı (görev
 * C). Backend `POST /tenant/users/{id}/password` TEK uçta hem "gövde boş →
 * rastgele üret + kaydet" hem "password verildi → onu kaydet" davranışını
 * birleştirdiği için burada "🎲 Oluştur" TEK tıkla (üret+kaydet+göster)
 * çalışır; elle giriş ayrı bir "Kaydet" adımı gerektirir. Legacy'nin ayrı
 * "üret → gözden geçir → kaydet" iki adımından FARKLI ama daha az tıklama —
 * backend'in tek-uç tasarımıyla tutarlı, kasıtlı bir sadeleştirme.
 *
 * `MembersTable.tsx` bu modali `open` prop'u OLMADAN, kosullu render ile
 * kullanir (yalniz secim yapilinca mount edilir) — bu yuzden `ModalShell`e
 * `open` sabit `true` verilir; kabuk Escape/backdrop/body-scroll-kilit
 * yonetimini uzerine alir.
 */
export function PasswordResetModal({ userId, email, onClose }: PasswordResetModalProps) {
  const mutation = useSetTenantUserPasswordMutation();
  const [manualPassword, setManualPassword] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [copyLabel, setCopyLabel] = useState<string | null>(null);

  function handleGenerate() {
    setValidationError(null);
    mutation.mutate({ userId, payload: {} }, { onSuccess: (res) => setResult(res.generated_password) });
  }

  function handleSave() {
    if (manualPassword.length < 8) {
      setValidationError(t("kullanicilar.password.too_short"));
      return;
    }
    setValidationError(null);
    mutation.mutate(
      { userId, payload: { password: manualPassword } },
      { onSuccess: () => setResult(manualPassword) },
    );
  }

  async function handleCopy() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setCopyLabel(t("kullanicilar.password.copied"));
    } catch {
      // Panoya erisim yoksa (izin/HTTPS/jsdom) sessizce yut — sifre zaten
      // ekranda gorunur durumda, kullanici elle secip kopyalayabilir.
    }
  }

  return (
    <ModalShell open title={t("kullanicilar.password.title")} onClose={onClose} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-ink">{t("kullanicilar.password.target", { email })}</p>

        {!result && (
          <>
            <label className="block">
              <span className="label">{t("kullanicilar.password.new_label")}</span>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualPassword}
                  onChange={(e) => setManualPassword(e.target.value)}
                  placeholder={t("kullanicilar.password.placeholder")}
                  autoComplete="new-password"
                  className="input flex-1"
                />
                <Button variant="outline" onClick={handleGenerate} disabled={mutation.isPending}>
                  {t("kullanicilar.password.generate")}
                </Button>
              </div>
            </label>

            {validationError && (
              <p role="alert" className="text-sm text-red">
                {validationError}
              </p>
            )}
            {mutation.isError && (
              <p role="alert" className="text-sm text-red">
                {errorMessage(mutation.error, t("common.error"))}
              </p>
            )}

            <Button variant="primary" onClick={handleSave} disabled={mutation.isPending}>
              {mutation.isPending ? t("kullanicilar.password.saving") : t("kullanicilar.password.save")}
            </Button>
          </>
        )}

        {result && (
          <Ribbon tone="warn">
            <p className="text-sm font-semibold text-ink">{t("kullanicilar.password.updated_title")}</p>
            <p className="mt-1 text-xs text-ink">{t("kullanicilar.password.updated_hint")}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="rounded bg-card px-2 py-1 text-sm">{result}</code>
              <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                {copyLabel ?? t("kullanicilar.password.copy")}
              </Button>
            </div>
          </Ribbon>
        )}

        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("common.close")}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
